import { useState, useEffect, useRef } from "react";
import useSignaling from "../WebRTCSignaling";

/*
  WebRTC pushing

  @param props (object)
  - uri (string):               WSS URL
  - streamBitrate (function):   desired bitrate of shown video track
  - stream (object):            reference to a MediaStream we want to export
 */
const useWebRTCBroadcaster = (props) => {
  // Reference to the WebRTC connections
  const peerConnRef = useRef(null);
  // Ready to send out WebRTC
  const [isReady, setReady] = useState(false);
  // Whether we are currently connected over WebRTC
  const [isStreaming, setIsStreaming] = useState(false);
  // Open websocket for signaling
  const [sendVideoBitrate, sendOfferSDP, sendStop, sendSeek] = useSignaling({
    uri: props.uri,
    onEvent: onEvent,
  });

  useEffect(() => {
    if (!isReady || !props.stream) {
      return;
    }
    // If we are already streaming - noop
    if (isStreaming) {
      return;
    }
    console.log(
      "starting push to ",
      props.stream,
      "with tracks ",
      props.stream.getTracks()
    );

    function iceCandidateError(e) {
      if (e.errorText == "STUN host lookup received error.")
        console.error("WebRTC is being blocked");
      else {
        console.error(e);
      }
    }

    // Set up WebRTC connection
    peerConnRef.current = new RTCPeerConnection();
    peerConnRef.onicecandidateerror = iceCandidateError;
    peerConnRef.current.addStream(props.stream);
    peerConnRef.current
      .createOffer({ offerToReceiveVideo: false, offerToReceiveAudio: false })
      .then(
        function (offer) {
          // console.log("Got offer.");
          // console.log(offer.sdp);
          let localOfferSDP = offer.sdp;
          peerConnRef.current.setLocalDescription(offer).then(
            function () {
              // console.log("Set the local offer.");
              sendVideoBitrate(props.streamBitrate);
              sendOfferSDP(localOfferSDP);
            },
            function (err) {
              console.error("Failed to set the local offer.");
              console.error(err);
            }
          );
        },
        function (err) {
          console.error("Failed to get offer.");
          console.error(err);
        }
      );
  }, [isReady]);

  // FUNCTIONS WEBRTC SIGNALING

  function onSignalingDisconnected() {
    setReady(false);
    setIsStreaming(false);
  }

  function onSignalingConnected() {
    setReady(true);
  }

  function onBitrateUpdate(ev) {
    console.log("Video bitrate update to:", ev.video_bitrate);
  }

  function onSignalingError(ev) {
    console.error(ev.message);
  }

  function onSignalingAnswerSuccess() {
    setIsStreaming(true);
  }

  function onSignalingAnswerError(error) {
    console.error("Failed to set the remote answer.", error);
  }

  function onSignalingSdp(ev) {
    if (!ev.result) {
      console.error("The media server rejected the stream");
      onSignalingDisconnected();
      return;
    }
    // console.log("Received answer SDP.");
    // console.log(ev.answer_sdp);
    var opts = { type: "answer", sdp: ev.answer_sdp };
    var answer = new RTCSessionDescription(opts);
    peerConnRef.current
      .setRemoteDescription(answer)
      .then(onSignalingAnswerSuccess, onSignalingAnswerError);
  }

  function onEvent(ev) {
    switch (ev.type) {
      case "on_connected": {
        onSignalingConnected();
        break;
      }
      case "on_message": {
        break;
      }
      case "on_disconnected": {
        onSignalingDisconnected();
        break;
      }
      case "on_answer_sdp": {
        onSignalingSdp(ev);
        break;
      }
      case "on_error": {
        onSignalingError(ev);
        break;
      }
      case "on_video_bitrate": {
        onBitrateUpdate(ev);
        break;
      }
      case "on_media_receive": {
        break;
      }
      default: {
        console.warn("Unhandled event:", ev.type, ev);
        break;
      }
    }
  }
};

export default useWebRTCBroadcaster;
